/**
 * locales/en.js — English (en-US) catalog, an overlay over the es-ES source of truth.
 *
 * The `es` catalog is the ultimate fallback: if a key is missing here, the storefront
 * falls back to the Spanish value (never empty, never the key). Any language other
 * than es/en is out of scope (REQ-032).
 */

export default {
  // Navbar
  'nav.products': 'All products',
  'nav.news': 'Offers',
  'nav.contact': 'Contact',
  'nav.collections': 'Collections',
  'nav.account': 'Account',
  'nav.myAccount': 'My account',
  'nav.viewCart': 'View cart',
  'nav.search': 'Search products',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.viewAll': 'View all',
  'nav.shippingSpain': 'GLS shipping within Spain',
  'nav.shippingIntl': 'GLS shipping within Spain',

  // Footer
  'footer.home': 'Home',
  'footer.contact': 'Contact',
  'footer.faq': 'FAQ',
  'footer.terms': 'Terms and conditions',
  'footer.privacy': 'Privacy policy',
  'footer.refunds': 'Refund policy',
  'footer.shippingPolicy': 'Shipping policy',

  // Footer columns
  'footer.colShop': 'Shop',
  'footer.colNav': 'Navigation',
  'footer.colLegal': 'Legal',
  'footer.colContact': 'Contact',
  'footer.products': 'Products',
  'footer.cart': 'Cart',

  // Language
  'language.select': 'Language',
  'language.es': 'Español',
  'language.en': 'English',

  // Home
  'home.heroCta': '5 Stars Collection',
  'home.categories': 'Categories',
  'home.buy': 'Shop',
  'home.viewAll': 'View all',
  'home.viewCaps': 'Shop caps',
  'home.viewAccessories': 'Shop accessories',
  'home.viewCatalog': 'View catalog',
  'home.latestDrops': 'Latest drops',
  'home.heroName': 'Main hero',
  'home.capsBannerName': 'Caps banner',
  'home.capsBannerSubtitle': 'Our signature piece: premium materials and minimalist aesthetic.',
  'home.shopCategory': 'SHOP COLLECTION',
  'home.showcaseProgress': '{title} progress',
  'home.stepOf': 'Step {index} of {total}',

  // Catalog / product
  'catalog.newIn': 'NEW IN',
  'catalog.offer': 'SALE',
  'catalog.soldOut': 'SOLD OUT',
  'catalog.viewAllFor': 'View all results for "{query}"',
  'catalog.noResults': 'No products found',
  'catalog.selectType': 'Select {name}',
  'catalog.sort': 'Sort',
  'catalog.category': 'Category',
  'catalog.results': 'Results',
  'catalog.onSale': 'On sale',
  'product.selected': 'Selected',
  'product.addToCart': 'Add to cart',
  'product.adding': 'Adding',
  'product.added': 'Added',
  'product.selectColor': 'Select color',
  'product.availableStock': 'Available stock',

  // Size recommender
  'size.ask': 'What size should I choose?',
  'size.dialog': 'Size recommender',
  'size.close': 'Close',
  'size.subtitle': 'Tell us your height and weight and we will recommend the size that fits this garment best.',
  'size.disclaimer': 'This recommendation is indicative. Please check the size guide.',
  'size.height': 'Your height',
  'size.weight': 'Your weight',
  'size.example': 'E.g. {value}',
  'size.recommend': 'Recommend size',
  'size.calculating': 'Calculating…',
  'size.recommended': 'We recommend',
  'size.closestAvailable': '(the closest size available for this product)',
  'size.alternatives': 'You may also like:',
  'size.size': 'Size',
  'size.chart': 'Size guide',
  'size.completeMeasurements': 'Enter your height and weight to get a recommendation.',
  'size.heightRange': 'Height must be between 100 and 250 cm.',
  'size.weightRange': 'Weight must be between 30 and 250 kg.',
  'size.unavailable': 'We could not calculate a recommendation for this product.',
  'size.calculate': 'Calculate',
  'size.recalculate': 'Recalculate',
  'size.gotIt': 'Got it',

  // Cart / checkout
  'cart.processing': 'Processing...',
  'cart.redirecting': 'Redirecting...',
  'cart.confirmingOrder': 'Confirming order...',
  'cart.redirectingPaypal': 'Redirecting to PayPal...',
  'cart.checkout': 'Checkout',
  'cart.added': 'Added',
  'cart.yourCart': 'Your cart',
  'cart.closeCart': 'Close cart',
  'cart.empty': 'Your cart is empty',
  'cart.subtotal': 'Subtotal',
  'cart.emptyDesc': 'Discover our products and start your purchase.',
  'cart.viewProducts': 'View products',
  'cart.orderSummary': 'Order summary',
  'cart.paymentMethod': 'Payment method',
  'cart.payWithPayPal': 'Pay with PayPal',
  'cart.payWithStripe': 'Card, Google Pay or Apple Pay',
  'cart.stripeUnavailable': 'Stripe payment is not available right now. Choose another method.',
  'cart.bankTransfer': 'Bank transfer',
  'cart.bankTransferHint': 'We send you the details',
  'cart.continueWithYourData': 'Or continue with your details',
  'cart.contact': 'Contact',
  'cart.emailHelpLabel': 'More info about the email',
  'cart.emailHelpText': 'We\'ll send the order confirmation and tracking to this email.',
  'cart.delivery': 'Delivery',
  'cart.deliveryCountry': 'Delivery country',
  'cart.countrySpain': 'Spain',
  'cart.companyOptional': 'Company (optional)',
  'cart.street': 'Street',
  'cart.addressHelpLabel': 'More info about the address',
  'cart.addressHelpText': 'Enter the street of your delivery address.',
  'cart.number': 'Number / door',
  'cart.addressDetails': 'Floor, door, stairs, etc. (optional)',
  'cart.city': 'City',
  'cart.province': 'Province',
  'cart.postalCode': 'Postal code',
  'cart.neighborhood': 'Delivery reference (optional)',
  'cart.phoneWithPrefix': 'Phone (+34)',
  'cart.phoneHelpLabel': 'More info about the phone',
  'cart.phoneHelpText': 'In case we need to contact you about your order. Include the prefix (+34).',
  'cart.nifCif': 'NIF / NIE / CIF',
  'cart.shippingMethod': 'Shipping method',
  'cart.shippingPlaceholder': 'Enter your address to see available shipping methods.',
  'cart.confirmOrder': 'Confirm order',
  'cart.payNow': 'Pay now',
  'cart.minPurchase': 'Minimum purchase {price}',
  'cart.continueShopping': 'Continue shopping',
  'cart.noStock': 'Not enough stock of {name}. Available stock: {stock}',
  'cart.couponPlaceholder': 'Discount code or gift card',
  'cart.apply': 'Apply',
  'cart.appliedTo': 'Applied to {eligible} of {total} products',
  'cart.shipping': 'Shipping',
  'cart.enterAddress': 'Enter your address',
  'cart.discount': 'Discount',
  'cart.total': 'Total',
  'cart.minReached': 'Minimum order {price} reached',
  'cart.minRemaining': 'Minimum order {price} — {remaining} to go',

  // Product detail
  'product.description': 'Description',
  'product.sizeFit': 'Size & fit',
  'product.shippingStandard': 'Standard',
  'product.notFound': 'Product not found',
  'product.outOfStock': 'out of stock',
  'product.shippingStandardDays': '3–7 business days',
  'product.shippingReturns': 'Shipping & returns',
  'product.shipStandardLine': 'Standard shipping: 3–7 business days',
  'product.noDescription': 'No description available for this product.',
  'product.notFoundDesc': 'The product you are looking for does not exist or is not available.',
  'product.backToProducts': 'Back to products',
  'product.closeColorPicker': 'Close color picker',
  'product.sizeLabel': 'Size: {value}',
  'product.composition': 'Composition: {value}',

  // Account / auth
  'auth.login': 'Sign in',
  'auth.register': 'Create account',
  'auth.accountAccess': 'Account access',
  'auth.recoverHere': 'Recover it here',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.signingIn': 'Signing in...',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirmPassword': 'Confirm password',
  'auth.firstName': 'First name',
  'auth.lastName': 'Last name',
  'auth.username': 'Username',
  'auth.nif': 'NIF / NIE',
  'auth.phone': 'Phone',
  'auth.creatingAccount': 'Creating account...',
  'account.myOrders': 'My Orders',
  'account.myAccount': 'My account',
  'account.logout': 'Sign out',
  'account.addresses': 'Shipping addresses',
  'account.personalData': 'Personal details',
  'account.nav.orders': 'Orders',
  'account.nav.settings': 'Settings',
  'account.nav.contact': 'Contact',
  'account.dashboard': 'My dashboard',
  'account.welcome': 'Welcome back, {name}!',
  'account.welcomeSubtitle': 'Here you can view your orders and manage your account.',
  'account.user': 'User',
  'account.totalOrders': 'Total orders',
  'account.lastOrder': 'Last order',
  'account.noOrders': 'No orders',
  'account.status': 'Status',
  'account.regularCustomer': 'Regular customer',
  'account.recentOrders': 'Recent orders',
  'account.viewAll': 'View all',
  'account.thOrderNo': 'Order No.',
  'account.thDate': 'Date',
  'account.thTotal': 'Total',
  'account.thStatus': 'Status',
  'account.thActions': 'Actions',
  'account.viewDetails': 'View details',
  'account.noRecentOrders': 'You have no recent orders',
  'account.startShopping': 'Start shopping',
  'account.errorLoadingOrders': 'Error loading your orders',
  'account.statusPending': 'Pending',
  'account.statusPaid': 'Paid',
  'account.statusPreparing': 'Preparing',
  'account.statusShipped': 'Shipped',
  'account.statusDelivered': 'Delivered',
  'account.statusCancelled': 'Cancelled',
  'account.orders.reqStatusPending': 'Pending',
  'account.orders.reqStatusApproved': 'Approved',
  'account.orders.reqStatusRejected': 'Rejected',
  'account.orders.reqStatusAwaitingReturn': 'Awaiting return',
  'account.orders.reqStatusReceived': 'Received',
  'account.orders.reqStatusConfirmed': 'Confirmed',
  'account.orders.reqStatusCompleted': 'Completed',

  // Account: addresses
  'addr.manageIntro': 'Manage your addresses to speed up your purchases',
  'addr.add': 'Add address',
  'addr.loading': 'Loading addresses...',
  'addr.noneTitle': 'You have no saved addresses',
  'addr.noneHint': 'Add an address to speed up your purchases.',
  'addr.addFirst': 'Add my first address',
  'addr.defaultBadge': 'Default',
  'addr.editAria': 'Edit',
  'addr.deleteAria': 'Delete',
  'addr.floorWord': 'Floor',
  'addr.aptWord': 'Apt.',
  'addr.cpWord': 'Postcode',
  'addr.setDefault': 'Set as default',
  'addr.modalEdit': 'Edit address',
  'addr.modalNew': 'New address',
  'addr.closeAria': 'Close',
  'addr.labelOpt': 'Label (optional)',
  'addr.labelPh': 'e.g. Home, Office, etc.',
  'addr.street': 'Street *',
  'addr.streetPh': 'Street name',
  'addr.number': 'Number *',
  'addr.floor': 'Floor',
  'addr.floorPh': 'e.g. 3',
  'addr.apartment': 'Apartment',
  'addr.aptPh': 'e.g. A',
  'addr.city': 'City *',
  'addr.cityPh': 'City',
  'addr.province': 'Province *',
  'addr.selectProvince': 'Select a province',
  'addr.postalCode': 'Postal code *',
  'addr.useDefault': 'Use as default address',
  'addr.cancel': 'Cancel',
  'addr.saveChanges': 'Save changes',
  'addr.addSubmit': 'Add address',
  'addr.updated': 'Address updated',
  'addr.added': 'Address added',
  'addr.saveError': 'Error saving the address',
  'addr.deleteConfirm': 'Delete the address "{name}"?',
  'addr.deleted': 'Address deleted',
  'addr.deleteError': 'Error deleting the address',
  'addr.defaultUpdated': 'Default address updated',
  'addr.defaultError': 'Error updating the default address',
  'addr.loadError': 'Error loading addresses',

  // Account: contact
  'contact.viewProfile': 'View profile',
  'account.contactSubtitle': 'Write to us about your order, account changes or any purchase query.',
  'account.contactSendTitle': 'Send us a message',
  'account.contactHint': 'We reply as soon as possible, Monday to Friday.',
  'account.contactName': 'Name',
  'account.contactNamePh': 'Your name',
  'account.contactSubject': 'Subject',
  'account.contactSubjectPh': 'What is it about?',
  'account.contactMessagePh': 'Tell us in detail...',
  'account.contactSent': 'Message sent successfully',
  'account.contactError': 'We could not send your message',

  // Account: orders
  'ord.subtitle': 'Track your purchases, deliveries and payments.',
  'ord.refresh': 'Refresh',
  'ord.filtersAria': 'Filter orders by status',
  'ord.loading': 'Loading your orders...',
  'ord.empty': 'You have no orders yet',
  'ord.goShop': 'Go to the store',
  'ord.number': 'Order #{id}',
  'ord.productOne': 'product',
  'ord.productsMany': 'products',
  'ord.unitOne': 'unit',
  'ord.unitsMany': 'units',
  'ord.each': 'each',
  'ord.subtotalLbl': 'Subtotal:',
  'ord.noProductsInOrder': 'There are no products in this order.',
  'ord.recipient': 'Recipient',
  'ord.phone': 'Phone',
  'ord.shipping': 'Shipping',
  'ord.address': 'Address',
  'ord.deliveryHome': 'Home delivery',
  'ord.pickupStore': 'Store pickup',
  'ord.productsTitle': 'Products',
  'ord.unnamed': 'Unnamed product',
  'ord.productFallback': 'Product',
  'ord.hideShipping': 'Hide shipping',
  'ord.viewShippingDetails': 'View shipping details',
  'ord.feeShipping': 'Shipping',
  'ord.totalLabel': 'Order total:',
  'ord.paymentMethod': 'Payment method',
  'ord.paymentStatus': 'Payment status',
  'ord.paid': 'Paid',
  'ord.paymentPending': 'Payment pending',
  'ord.paymentPayPal': 'PayPal',
  'ord.paymentStripe': 'Card / Stripe',
  'ord.paymentBankTransfer': 'Bank transfer',
  'ord.paymentOnline': 'Online payment',
  'ord.paymentCash': 'Cash',
  'ord.paymentUnknown': 'Not specified',
  'ord.coupon': 'Coupon {code}',
  'ord.discountApplied': 'Discount applied',
  'ord.shipService': 'Service',
  'ord.shipCost': 'Cost',
  'ord.shipEstimated': 'Estimated delivery',
  'ord.shipDays': 'days',
  'ord.shipPostal': 'Postal code',
  'ord.redirecting': 'Redirecting...',
  'ord.payLinkError': 'Could not generate the payment link.',
  'ord.payStartError': 'Error starting the payment.',
  'ord.updated': 'Orders updated',
  'ord.notLoggedIn': 'You are not signed in',
  'ord.loadError': 'Error loading your orders',
  'ord.unknownError': 'Unknown error',
  'ord.paypalOrderIdError': 'Could not identify the PayPal order.',
  'ord.emailDetailTitle': 'Receipt sent by email',
  'ord.emailDetailPart1': 'Your order #{id} now appears in this section.',
  'ord.emailSentTo': 'We sent your purchase receipt to {email}.',
  'ord.emailSentRegistered': 'We sent your purchase receipt to your registered email.',
  'ord.gotIt': 'Got it',
  'ord.filterAll': 'All',
  'ord.filterReview': 'Under review',
  'ord.filterPaymentPending': 'Payment to confirm',
  'ord.filterPaid': 'Paid',
  'ord.filterPreparing': 'Preparing',
  'ord.filterShipped': 'Shipped',
  'ord.filterDelivered': 'Delivered',
  'ord.filterCancelled': 'Cancelled',
  'ord.stPending': 'Under review',
  'ord.stPaymentPending': 'Payment to confirm',
  'ord.stPaid': 'Paid',
  'ord.stPreparing': 'Preparing',
  'ord.stShipped': 'Shipped',
  'ord.stDelivered': 'Delivered',
  'ord.stCancelled': 'Cancelled',
  'ord.retVerifyFailTitle': 'We could not verify the payment',
  'ord.retVerifyFailMsg': 'No order was found for the provider return.',
  'ord.retCheckingTitle': 'Verifying payment for order #{id}',
  'ord.retCheckingMsg': 'We are syncing the provider confirmation with your orders.',
  'ord.retNotApprovedTitle': 'Payment not approved for order #{id}',
  'ord.retNotApprovedMsg': 'The provider reported the payment was not approved. You can try paying again from this list.',
  'ord.retConfirmedTitle': 'Payment confirmed for order #{id}',
  'ord.retConfirmedMsg': 'We updated your order. You can now see it marked as paid in the list.',
  'ord.retPendingTitle': 'Payment to confirm for order #{id}',
  'ord.retPendingMsg': 'We are waiting for the payment confirmation. Check your orders again in a few minutes.',
  'ord.retPaypalUnclaimedMsg': 'PayPal received the payment, but it is still unclaimed in the seller account. Once it is accepted or completed in PayPal, the order can move to paid.',
  'ord.retErrorTitle': 'We could not verify the payment for order #{id}',
  'ord.retErrorMsg': 'Check your orders or try refreshing the list in a few seconds.',

  // Account: settings
  'set.accountTitle': 'Account settings',
  'set.accountSubtitle': 'Manage your personal data and account security.',
  'set.tabsAria': 'Settings sections',
  'set.dataHint': 'This data is used for your orders and invoicing.',
  'set.lastName': 'Last name',
  'set.lastNamePh': 'Your last name',
  'set.emailPh': 'you@email.com',
  'set.verify': 'Verify',
  'set.phoneHint': 'Include the international prefix (+34).',
  'set.dniHint': 'Tax document for orders and invoicing.',
  'set.companyPh': 'Company name',
  'set.verifyCode': 'Verification code',
  'set.cancelEmailAria': 'Cancel email change',
  'set.verifyCodeHint': 'We send a verification code to the new email.',
  'set.emailAlert': 'To change your email, enter the verification code sent to {email}',
  'set.saving': 'Saving...',
  'set.changePassword': 'Change password',
  'set.pwdHint': 'Use a strong password that you do not use on other sites.',
  'set.currentPassword': 'Current password',
  'set.currentPwdPh': 'Enter your current password',
  'set.newPwdPh': 'Enter your new password',
  'set.confirmPwdPh': 'Confirm your new password',
  'set.hidePassword': 'Hide password',
  'set.showPassword': 'Show password',
  'set.forgotPwd': 'Forgot your password? Recover it here',
  'set.changingPwd': 'Changing...',
  'set.secVerification': 'Security verification',
  'set.secModalText': 'Please enter your password to continue with the email change.',
  'set.modalPwdPh': 'Your current password',
  'set.verifying': 'Verifying...',
  'set.continueBtn': 'Continue',
  'set.recoveryText1': 'We will send a verification code to {email}.',
  'set.recoveryText2': 'Enter the code you received by email and choose your new password.',
  'set.confirmPwd': 'Confirm password',
  'set.processing': 'Processing...',
  'set.errNameMin': 'The name must be at least 2 characters long',
  'set.errLastNameMin': 'The last name must be at least 2 characters long',
  'set.errEmail': 'Enter a valid email',
  'set.errDni': 'Enter a valid NIF, NIE or CIF',
  'set.errPhone': 'Enter a valid phone number (at least 8 digits)',
  'set.errMinConfig': 'Error updating the minimum purchase amount',
  'set.errDollarRate': 'Error updating the dollar rate',
  'set.errCenter': 'Error processing the distribution center {name}',
  'set.profileUpdated': 'Profile updated successfully',
  'set.profileUpdateError': 'Error updating your profile',
  'set.errEnterPwd': 'Please enter your password',
  'set.codeSentNewEmail': 'Verification code sent to the new email',
  'set.emailExists': 'This email is already registered. Please use another email.',
  'set.wrongPassword': 'Incorrect password',
  'set.errRequestCode': 'Error requesting the verification code',
  'set.errNoEmail': 'No email found for your account',
  'set.codeSentEmail': 'Verification code sent to your email',
  'set.errRequestRecovery': 'Error requesting the recovery code',
  'set.errCode6': 'The code must be 6 digits long',
  'set.errPwdMin': 'The password must be at least 6 characters long',
  'set.errPwdMismatch': 'Passwords do not match',
  'set.pwdRecovered': 'Password reset successfully',
  'set.errResetPwd': 'Error resetting your password',
  'set.emailUpdated': 'Email updated successfully',
  'set.errCodeInvalid': 'The verification code is invalid or has expired',
  'set.errUpdateEmail': 'Error updating the email',
  'set.errPwdCurrent': 'Enter your current password',
  'set.errPwdNew': 'Enter the new password',
  'set.errPwdNewMin': 'The new password must be at least 6 characters long',
  'set.pwdUpdated': 'Password updated successfully',
  'set.errChangePwd': 'Error changing the password',
  'set.errLoadStoreConfig': 'Could not load the store configuration',
  'set.errLoadData': 'Error loading data',

  // FAQ / legal
  'faq.title': 'Frequently asked questions',
  'faq.viewAll': 'View all',
  'faq.loading': 'Loading…',
  'legal.privacy': 'Privacy policy',
  'legal.terms': 'Terms and conditions',
  'legal.refunds': 'Refund policy',
  'legal.shipping': 'Shipping',

  // Contact
  'contact.title': 'Contact',
  'contact.subject': 'Subject',
  'contact.send': 'Send inquiry',
  'contact.sendMessage': 'Send message',
  'contact.sending': 'Sending...',
  'contact.customerService': 'Customer service',
  'contact.directChannels': 'Direct channels',
  'contact.message': 'Message',

  // Password reset
  'auth.recoverPassword': 'Recover password',
  'auth.sendCode': 'Send code',
  'auth.newPassword': 'New password',
  'auth.setNewPassword': 'Set your new password',
  'auth.code6': '6-digit code',
  'auth.confirmNewPassword': 'Confirm new password',
  'auth.resetPassword': 'Reset password',
  'auth.resetting': 'Resetting...',
  'auth.passwordReset': 'Password reset',
  'auth.accountActivated': 'Account activated',
  'auth.activationError': 'Could not activate the account',
  'auth.verifyingEmail': 'Verifying email',
  'auth.validatingLink': 'Validating the activation link...',

  // Blog
  'blog.title': 'Blog',
  'blog.loading': 'Loading articles...',
  'blog.noResults': 'No articles found',
  'blog.tryOtherFilters': 'Try other search filters',
  'blog.share': 'Share article',
  'blog.related': 'Related Articles',
  'blog.notFound': 'Article not found',
  'blog.notFoundDesc': 'The article you are looking for does not exist or is not available.',

  // Catalog: browser/drawer/empty-state texts
  'catalog.refine': 'Refine',
  'catalog.loading': 'Loading',
  'catalog.defaultTitle': 'New arrivals',
  'catalog.filtersAria': 'Catalog filters',
  'catalog.closeFilters': 'Close filters',
  'catalog.sortFeatured': 'Featured',
  'catalog.sortPriceAsc': 'Lowest price',
  'catalog.sortPriceDesc': 'Highest price',
  'catalog.size': 'Size',
  'catalog.sizeNumeric': 'Numeric size',
  'catalog.clear': 'Clear',
  'catalog.clearFilters': 'Clear filters',
  'catalog.viewProducts': 'View {count} products',
  'catalog.noResultsHint': 'Try adjusting your filters.',
  'catalog.youMayLike': 'You may also like',
  'catalog.recProgress': 'Recommendations progress',
  'catalog.recStep': 'Step {index} of {total}',
  'catalog.viewProduct': 'View product',
  'catalog.viewColor': 'View {name}',
  'catalog.viewImage': 'View image {index}',
  'catalog.imageAlt': 'image {index}',
  'catalog.alternateView': 'alternative view',
  'catalog.colorsAria': 'Available colors',
  'catalog.colorsCount': '{count} colors',

  // Search drawer
  'search.title': 'Search',
  'search.placeholder': 'What are you looking for?',
  'search.close': 'Close search',
  'search.clear': 'Clear search',
  'search.noResults': 'No results',
  'search.noResultsFor': 'We could not find products for "{query}"',
  'search.viewFullCatalog': 'View full catalog',
  'search.typeToStart': 'Type to search products',
  'search.viewAll': 'View all',
  'search.viewAllResults': 'View all results for "{query}"',
  'search.result': 'result',
  'search.results': 'results',

  // Cart: accessibility
  'cart.qtyDecrease': 'Decrease quantity',
  'cart.qtyIncrease': 'Increase quantity',
  'cart.removeItem': 'Remove product',

  // Page titles (storefront document.title)
  'page.home': 'Home',
  'page.products': 'Products',
  'page.product': 'Product',
  'page.blog': 'Blog',
  'page.terms': 'Terms & Conditions',
  'page.privacy': 'Privacy Policy',
  'page.refunds': 'Refunds Policy',
  'page.shipping': 'Shipping',
  'page.contact': 'Contact',
  'page.cart': 'Shopping Cart',
  'page.paymentWait': 'Preparing payment',
  'page.login': 'Sign In',
  'page.register': 'Create Account',
  'page.verifyEmail': 'Verify Email',
  'page.recoverPassword': 'Reset Password',
  'page.gls': 'GLS pickup points',


  // ── TASK-032 keys imported on merge (used by integrated code) ──
  "account.orders.back": "Back",
  "account.orders.cancelOrder": "Cancel an order",
  "account.orders.cancelOrderDesc": "If you changed your mind and the order has not been shipped yet.",
  "account.orders.cancellation": "Cancellation",
  "account.orders.delivery": "Delivery",
  "account.orders.email": "Email",
  "account.orders.helpChooseAction": "Choose what you need to do with your order.",
  "account.orders.helpReason": "Tell us the reason. Before sending the request, please review the",
  "account.orders.needHelp": "Need help?",
  "account.orders.noAddress": "No address on file",
  "account.orders.noEligibleOrders": "You do not have any orders that can be cancelled or returned right now.",
  "account.orders.paymentFailedText": "You can view your order and retry the payment from this section.",
  "account.orders.paymentFailedTitle": "The payment could not be completed",
  "account.orders.paymentPendingMsg": "Your order is pending payment.",
  "account.orders.providerRejected": "The payment provider reported that the payment was not approved.",
  "account.orders.reasonPlaceholder": "Request reason (required)",
  "account.orders.refundPolicyLink": "returns and refunds policy",
  "account.orders.refundPolicySuffix": "(deadlines and conditions).",
  "account.orders.requestError": "Error sending the request. Please try again.",
  "account.orders.requestSentCancellation": "Cancellation request sent. We will notify you by email.",
  "account.orders.requestSentReturn": "Return request sent. We will notify you by email.",
  "account.orders.retryPayment": "Retry payment",
  "account.orders.return": "Return",
  "account.orders.returnProducts": "Return products",
  "account.orders.returnProductsDesc": "If you already received the order and want to return it.",
  "account.orders.selectOrder": "Select the order",
  "account.orders.sendRequest": "Send request",
  "account.orders.sending": "Sending...",
  "account.orders.status": "Status",
  "account.orders.stripeStartError": "Could not start the card payment.",
  "account.orders.total": "Total",
  "account.orders.verifyProviderError": "We could not verify the payment with the provider.",
  "account.orders.viewMyOrder": "View my order",
  "admin.nav.abandonedCarts": "Abandoned carts",
  "admin.nav.accountSettings": "Settings",
  "admin.nav.addProduct": "Add product",
  "admin.nav.addresses": "Addresses",
  "admin.nav.adminSection": "ADMINISTRATION",
  "admin.nav.categories": "Categories",
  "admin.nav.closeMenu": "Close menu",
  "admin.nav.coupons": "Coupons",
  "admin.nav.design": "Design",
  "admin.nav.general": "General",
  "admin.nav.legal": "Legal content",
  "admin.nav.logout": "Sign out",
  "admin.nav.marketing": "Marketing",
  "admin.nav.metrics": "Metrics",
  "admin.nav.myAccount": "My Account",
  "admin.nav.myOrders": "My Orders",
  "admin.nav.newSale": "New sale",
  "admin.nav.offers": "Offers",
  "admin.nav.openMenu": "Open menu",
  "admin.nav.pickElement": "Select element",
  "admin.nav.products": "Products",
  "admin.nav.productsList": "Products list",
  "admin.nav.profileSection": "PROFILE",
  "admin.nav.remitos": "Delivery notes",
  "admin.nav.requests": "Requests",
  "admin.nav.sales": "Sales",
  "admin.nav.salesList": "Sales list",
  "admin.nav.shipping": "Shipping",
  "admin.nav.sizeCharts": "Size charts",
  "admin.nav.store": "Store",
  "admin.nav.storeSettings": "Settings",
  "admin.nav.titleCoupons": "Coupons",
  "admin.nav.titleCustomers": "Customers",
  "admin.nav.titleDashboard": "Control panel",
  "admin.nav.titleOffers": "Offers",
  "admin.nav.titleOrders": "My Orders",
  "admin.nav.titleOverview": "General panel",
  "admin.nav.titleProducts": "Products",
  "admin.nav.titleSales": "Sales",
  "admin.nav.titleSettings": "Settings",
  "admin.nav.titleStats": "Statistics",
  "admin.nav.titleStore": "Store",
  "admin.nav.translations": "Translations",
  "admin.nav.users": "Users",
  "admin.nav.usersList": "Users list",
  "checkout.couponApplied": "Coupon applied: {discount}",
  "checkout.couponAppliedTo": " (applied to {applied} of {total} products)",
  "checkout.couponError": "Error validating the coupon",
  "checkout.couponNotApplicable": "This coupon cannot be applied to any of the products in your cart. It is intended for products in specific categories.",
  "checkout.couponRemoved": "Coupon removed",
  "checkout.couponRemovedNoEligible": "The coupon has been removed because there are no eligible products in the cart",
  "checkout.discountAmount": "{amount} off",
  "checkout.discountPercent": "{percent}% off",
  "checkout.discountUpTo": " (up to {amount})",
  "checkout.eligibleOnly": " (only for eligible products)",
  "checkout.insufficientStock": "Not enough stock for {name}. Available stock: {stock}",
  "checkout.invalidCoupon": "Invalid coupon",
  "checkout.loginRequiredCoupon": "You must sign in to use a coupon",
  "checkout.noStock": "{name} is out of stock",
  "checkout.orderError": "Error processing the order",
  "checkout.pricesChanged": "The price of {count} product(s) has changed and was updated: {names}",
  "checkout.validate.deliveryRequired": "Select a delivery method",
  "checkout.validate.docInvalid": "Enter a valid tax document",
  "checkout.validate.docRequired": "NIF/NIE/CIF is required",
  "checkout.validate.emailInvalid": "Enter a valid email",
  "checkout.validate.emailRequired": "Email is required",
  "checkout.validate.nameMinLength": "Name must be at least 3 characters",
  "checkout.validate.nameRequired": "Name is required",
  "checkout.validate.paymentRequired": "Select a payment method",
  "checkout.validate.phoneInvalid": "Enter a valid phone number (minimum 8 digits)",
  "checkout.validate.phoneRequired": "Phone is required",
  "checkout.validate.provinceRequired": "Select a province",
  "notFound.backHome": "Back to home",
  "notFound.desc": "Sorry, the page you are looking for does not exist or has been moved.",
  "notFound.title": "Page not found",
  "product.lastUnits": "Last {stock} units",
  "product.selectVariant": "Select {name}",
  "welcomePopup.notNow": "Not now",
}
